import { tz } from "@date-fns/tz";
import { addDays, format, parseISO } from "date-fns";
import { z } from "zod";
import { getCsrfToken } from "@/lib/csrf";
import type { TherapistAddress } from "@/types/admin-portal/therapist";

// Relocation dates are WIB calendar days as "YYYY-MM-DD", the form Go reads and writes;
// strings of that shape compare in date order.
const nextDay = (day: string) =>
	format(addDays(parseISO(day), 1), "yyyy-MM-dd");

/**
 * The earliest day a new move may start: tomorrow in `timezone` (R7), and after every
 * start already in the chain, since Go only lets the chain grow forward.
 */
export function minEffectiveDate(
	now: Date,
	timezone: string,
	entries: { effectiveFrom: string | null }[],
) {
	const tomorrow = nextDay(format(now, "yyyy-MM-dd", { in: tz(timezone) }));
	const latest = entries
		.map((entry) => entry.effectiveFrom)
		.filter((day): day is string => !!day)
		.sort()
		.at(-1);
	return latest && nextDay(latest) > tomorrow ? nextDay(latest) : tomorrow;
}

const NEW_HOME = z.object({
	locationId: z.number().int().positive(),
	address: z.string().trim().min(1),
	postalCode: z.string().optional(),
	notes: z.string().optional(),
	// Go refuses a missing coordinate or a zero half; the dialog parses "lat, lng" before this.
	coordinate: z
		.object({ lat: z.number(), lng: z.number() })
		.nullable()
		.transform((pin, ctx) => {
			if (pin && pin.lat !== 0 && pin.lng !== 0) return pin;
			ctx.addIssue({ code: "custom", message: "coordinate" });
			return z.NEVER;
		}),
});

// Go's address body; a blank postal code or note is sent as null, which clears it.
const goAddress = (home: z.output<typeof NEW_HOME>) => ({
	location_id: home.locationId,
	latitude: home.coordinate.lat,
	longitude: home.coordinate.lng,
	address: home.address,
	postal_code: home.postalCode?.trim() || null,
	notes: home.notes?.trim() || null,
});

const relocationSchema = (minDate: string) =>
	z
		.object({
			effectiveDate: z
				.string()
				.regex(/^\d{4}-\d{2}-\d{2}$/)
				.refine((day) => day >= minDate, "date"),
			reason: z.string().trim().min(1, "reason"),
			target: z.discriminatedUnion("mode", [
				z.object({ mode: z.literal("new"), address: NEW_HOME }),
				z.object({
					mode: z.literal("existing"),
					addressId: z
						.number()
						.int()
						.positive()
						.nullable()
						.transform((id, ctx) => {
							if (id) return id;
							ctx.addIssue({ code: "custom", message: "address_id" });
							return z.NEVER;
						}),
				}),
			]),
		})
		// Rails forwards these params as Go's body; exactly one of address / address_id.
		.transform(({ effectiveDate, reason, target }) => ({
			effective_date: effectiveDate,
			...(target.mode === "existing"
				? { address_id: target.addressId }
				: { address: goAddress(target.address) }),
			reason,
		}));

export type RelocationInput = z.input<ReturnType<typeof relocationSchema>>;
export type RelocationRequest = z.output<ReturnType<typeof relocationSchema>>;

/** Validates the dialog and shapes the params Rails forwards to Go. */
export const relocationRequest = (input: RelocationInput, minDate: string) =>
	relocationSchema(minDate).safeParse(input);

// A correction keeps the row's dates; Go replaces the whole address, so every field is sent.
const correctionSchema = z
	.object({ reason: z.string().trim().min(1, "reason"), address: NEW_HOME })
	.transform(({ reason, address }) => ({
		address: goAddress(address),
		reason,
	}));

export type CorrectionInput = z.input<typeof correctionSchema>;
export type CorrectionRequest = z.output<typeof correctionSchema>;

/** Validates the correction dialog and shapes the params Rails forwards to Go. */
export const correctionRequest = (input: CorrectionInput) =>
	correctionSchema.safeParse(input);

/** The dialogs' fetch to Rails, so a 409 with conflicts comes back to the open dialog. */
export async function sendJson(
	url: string,
	body: unknown,
	method: "POST" | "PATCH" | "DELETE" = "POST",
) {
	const response = await fetch(url, {
		method,
		headers: {
			"Content-Type": "application/json",
			Accept: "application/json",
			"X-CSRF-Token": getCsrfToken(),
		},
		body: JSON.stringify(body),
	});
	const json = await response.json().catch(() => ({}));
	return {
		ok: response.ok,
		status: response.status,
		message: json.message as string | undefined,
		error: json.error as string | undefined,
		data: json.data as RelocationCheck | undefined,
	};
}

export type ConflictKind = "OUT_OF_NEW_AREA" | "TOO_FAR_FROM_NEW_HOME";

/** A booked visit the move breaks, from Go's TherapistRelocationCheck (camelCased by Rails). */
export interface RelocationConflict {
	appointmentId: string;
	registrationNumber: string;
	visitNumber: number;
	patientName: string;
	/** WIB calendar day, YYYY-MM-DD */
	visitDate: string;
	/** WIB, HH:mm */
	visitTime: string;
	kinds: ConflictKind[];
	visitCity: string | null;
	distanceKm: number | null;
	durationMin: number | null;
}

/** Go's preview data, and the error of a save refused with 409. */
export interface RelocationCheck {
	effectiveDate: string;
	conflicts: RelocationConflict[];
}

/**
 * Whether a save refused with 409 may be resent with acknowledge_conflicts. Go's yes covers
 * every conflict at save time, so it is only given for visits the admin has already seen.
 */
export const onlySeenConflicts = (
	reported: RelocationConflict[],
	seen: RelocationConflict[],
) => {
	const seenIds = new Set(seen.map((conflict) => conflict.appointmentId));
	return reported.every((conflict) => seenIds.has(conflict.appointmentId));
};

export type EntryStatus = "CURRENT" | "SCHEDULED" | "ENDED";

/** The address Go stores for a timeline entry (camelCased by Rails). */
export interface TimelinePlace {
	locationId: number;
	city: string;
	latitude: number;
	longitude: number;
	address: string;
	postalCode: string | null;
	notes: string | null;
}

/** One row of Go's GET /admin/therapists/:id/addresses/timeline, oldest first. */
export interface TimelineEntry {
	therapistAddressId: number;
	status: EntryStatus;
	active: boolean;
	/** WIB calendar day, YYYY-MM-DD; null on the first home, which has no start */
	effectiveFrom: string | null;
	/** WIB calendar day, YYYY-MM-DD; the last day the row applies, computed by Go */
	effectiveUntil: string | null;
	cancellable: boolean;
	address: TimelinePlace;
	scheduledBy: string | null;
	/** admin name, else email; null when the user is not an admin here */
	scheduledByName: string | null;
	scheduledReason: string | null;
	scheduledAt: string | null;
}

export interface Timeline {
	today: string;
	entries: TimelineEntry[];
}

/**
 * Go's timeline rebuilt from the Rails rows, for when Go has not answered: the relocation chain
 * (managedByRelocation mirrors Go's chain) by start day, else the active row alone. The status is read
 * off the dates against `today`, so on the day a move takes effect it can differ from Go's.
 */
export function railsEntries(
	rows: TherapistAddress[],
	today: string,
): TimelineEntry[] {
	const chain = rows.filter((row) => row.managedByRelocation);
	// both are fresh arrays from filter, so sorting in place touches no prop
	return (chain.length ? chain : rows.filter((row) => row.active))
		.sort((a, b) =>
			(a.effectiveFrom ?? "").localeCompare(b.effectiveFrom ?? ""),
		)
		.map((row) => ({
			therapistAddressId: row.therapistAddressId,
			status: row.active
				? "CURRENT"
				: row.effectiveFrom && row.effectiveFrom > today
					? "SCHEDULED"
					: "ENDED",
			active: row.active,
			effectiveFrom: row.effectiveFrom ?? null,
			effectiveUntil: row.effectiveUntil ?? null,
			cancellable: false,
			address: {
				locationId: row.locationId,
				city: row.location.city,
				latitude: row.latitude,
				longitude: row.longitude,
				address: row.address,
				postalCode: row.postalCode,
				notes: row.notes ?? null,
			},
			scheduledBy: null,
			scheduledByName: null,
			scheduledReason: null,
			scheduledAt: null,
		}));
}

/**
 * The days Ubah tanggal may pick, as Go bounds them: from tomorrow (R7), after the previous row's
 * start, and before the next move's start. `max` is null for the last move. `current`, the start
 * the move already has, is refused too: Go would answer 200 for a write that changes nothing.
 */
export function rescheduleBounds(
	now: Date,
	timezone: string,
	entries: Pick<TimelineEntry, "therapistAddressId" | "effectiveFrom">[],
	entry: Pick<TimelineEntry, "therapistAddressId" | "effectiveFrom">,
) {
	const at = entries.findIndex(
		(other) => other.therapistAddressId === entry.therapistAddressId,
	);
	const previous = at > 0 ? [entries[at - 1]] : [];
	const next = entries[at + 1]?.effectiveFrom;
	return {
		min: minEffectiveDate(now, timezone, previous),
		max: next ? format(addDays(parseISO(next), -1), "yyyy-MM-dd") : null,
		current: entry.effectiveFrom,
	};
}

const rescheduleSchema = ({
	min,
	max,
	current,
}: ReturnType<typeof rescheduleBounds>) =>
	z
		.object({
			effectiveDate: z
				.string()
				.regex(/^\d{4}-\d{2}-\d{2}$/)
				.refine(
					(day) => day >= min && (!max || day <= max) && day !== current,
					"date",
				),
			reason: z.string().trim().min(1, "reason"),
		})
		.transform(({ effectiveDate, reason }) => ({
			effective_date: effectiveDate,
			reason,
		}));

export type RescheduleRequest = z.output<ReturnType<typeof rescheduleSchema>>;

/** Validates Ubah tanggal and shapes the params Rails forwards to Go. */
export const rescheduleRequest = (
	input: z.input<ReturnType<typeof rescheduleSchema>>,
	bounds: ReturnType<typeof rescheduleBounds>,
) => rescheduleSchema(bounds).safeParse(input);

/** A therapist address as the edit page serializes it. */
export interface SavedAddress {
	id: number;
	locationId: number;
	latitude: number;
	longitude: number;
	address: string;
	postalCode?: string | null;
	notes?: string | null;
	active: boolean;
	effectiveFrom?: string | null;
	effectiveUntil?: string | null;
}

const sameText = (a?: string | null, b?: string | null) =>
	(a ?? "").trim() === (b ?? "").trim();

/**
 * X5: the Alamat Lain rows to hide because Go copied them into a dated row (D5) and the copy is
 * what the timeline shows. Matched on content, so a copy fixed through Perbaiki brings its
 * original back.
 */
export function hiddenCopyIds(rows: SavedAddress[]) {
	const dated = rows.filter((row) => row.effectiveFrom || row.effectiveUntil);
	return new Set(
		rows
			.filter(
				(row) =>
					!row.active &&
					!row.effectiveFrom &&
					!row.effectiveUntil &&
					dated.some(
						(copy) =>
							copy.locationId === row.locationId &&
							copy.latitude === row.latitude &&
							copy.longitude === row.longitude &&
							sameText(copy.address, row.address) &&
							sameText(copy.postalCode, row.postalCode) &&
							sameText(copy.notes, row.notes),
					),
			)
			.map((row) => row.id),
	);
}

/** The fields of an Alamat Lain the form's Simpan writes. */
export interface DraftAddress {
	id?: string | number;
	country: string;
	state: string;
	city: string;
	postalCode?: string;
	address: string;
	active: boolean;
	lat?: number | null;
	lng?: number | null;
}

export type PendingKind = "new" | "edit" | "delete" | "today";

const sameDraft = (a: DraftAddress, b: DraftAddress) =>
	a.country === b.country &&
	a.state === b.state &&
	a.city === b.city &&
	sameText(a.postalCode, b.postalCode) &&
	sameText(a.address, b.address) &&
	(a.lat ?? null) === (b.lat ?? null) &&
	(a.lng ?? null) === (b.lng ?? null);

/**
 * Every Alamat Lain change waiting for the form's Simpan (X13, X14), in list order with removed
 * rows last. A row picked as utama today counts as that, whatever else changed on it.
 */
export function pendingAddressChanges<T extends DraftAddress>(
	saved: T[],
	current: T[],
): { kind: PendingKind; row: T }[] {
	const byId = new Map(saved.map((row) => [String(row.id), row]));
	const currentIds = new Set(current.map((row) => String(row.id)));
	const changes = current.flatMap((row): { kind: PendingKind; row: T }[] => {
		const before = row.id == null ? undefined : byId.get(String(row.id));
		if (!before) return [{ kind: "new", row }];
		if (row.active) return [{ kind: "today", row }];
		return sameDraft(before, row) ? [] : [{ kind: "edit", row }];
	});
	const removed = saved
		.filter((row) => !currentIds.has(String(row.id)))
		.map((row) => ({ kind: "delete" as PendingKind, row }));
	return [...changes, ...removed];
}

const shift = (day: string, days: number) =>
	format(addDays(parseISO(day), days), "yyyy-MM-dd");

/**
 * The days the garis waktu spans (X12): about four months back to two after the latest start, so a
 * long chain stays legible; `hidden` counts the homes that ended before it. `showAll` reaches back
 * to the first start instead.
 */
export function timelineWindow(
	entries: Pick<TimelineEntry, "effectiveFrom" | "effectiveUntil">[],
	today: string,
	{ narrow = false, showAll = false } = {},
) {
	const starts = entries
		.map((entry) => entry.effectiveFrom)
		.filter((day): day is string => !!day)
		.sort();
	const latest = starts.at(-1);
	const to = shift(latest && latest > today ? latest : today, narrow ? 40 : 75);
	let from = shift(today, narrow ? -50 : -115);
	if (showAll && starts[0] && starts[0] < from) from = shift(starts[0], -10);
	from = `${from.slice(0, 8)}01`;
	const hidden = entries.filter(
		(entry) => entry.effectiveUntil && entry.effectiveUntil < from,
	).length;
	return { from, to, hidden };
}
